import { useEffect, useMemo, useState } from 'react';
import { FriendAvatarsStack } from '../ui/FriendAvatarsStack';
import { HopperIcon } from '../ui/icons/HopperIcon';
import { VENUE_CATEGORY_OPTIONS, type VenueCategory } from '../venueCategories';
import {
  fetchVenuePhotos,
  type LegacyVenueOpeningHours,
  type VenueDetailData,
  type VenueOpeningHour,
  type VenuePhoto,
} from '../venueDetailApi';
import {
  PRICE_TIER_LABELS,
  type VerifiedReceiptPrice,
  verifiedReceiptPricePrimary,
  verifiedReceiptPriceSecondary,
} from './VerifiedAverageCheckBridge';
import './map-gallery-avatar-fix.css';

export type MapPreviewVenue = {
  id: string;
  name: string | null;
  address: string | null;
  category: VenueCategory | null;
  opening_status: 'open' | 'closed' | 'unknown';
  verified_receipt_price: VerifiedReceiptPrice | null;
};

type PreviewCommonProps = {
  venue: MapPreviewVenue;
  detail: VenueDetailData | null;
  loading: boolean;
  authenticated: boolean;
  saved: boolean;
  saving: boolean;
  onToggleSaved: () => void;
  onOpen: () => void;
  onOpenOffers: () => void;
};

type MobilePreviewProps = PreviewCommonProps & {
  expanded: boolean;
  onClose: () => void;
};

type PhotoPreview = {
  items: VenuePhoto[];
  hasMore: boolean;
};

const photoPreviewPromises = new Map<string, Promise<PhotoPreview>>();

function selectedVenuePhotos(venueId: string): Promise<PhotoPreview> {
  const cached = photoPreviewPromises.get(venueId);
  if (cached) return cached;

  const request = fetchVenuePhotos(venueId, null, 4)
    .then((response) => ({ items: response.data.slice(0, 3), hasMore: response.meta.has_more || response.data.length > 3 }))
    .catch(() => ({ items: [], hasMore: false }));

  photoPreviewPromises.set(venueId, request);
  return request;
}

function useSelectedVenuePhotos(venueId: string): PhotoPreview {
  const [preview, setPreview] = useState<PhotoPreview>({ items: [], hasMore: false });

  useEffect(() => {
    let active = true;
    setPreview({ items: [], hasMore: false });

    void selectedVenuePhotos(venueId).then((next) => {
      if (active) setPreview(next);
    });

    return () => {
      active = false;
    };
  }, [venueId]);

  return preview;
}

function categoryLabel(category: VenueCategory | null): string {
  if (!category) return 'Место';
  return VENUE_CATEGORY_OPTIONS.find((option) => option.value === category)?.label ?? 'Место';
}

function openingStatusLabel(status: MapPreviewVenue['opening_status']): string {
  if (status === 'open') return 'Открыто';
  if (status === 'closed') return 'Закрыто';
  return 'Время не указано';
}

function weekdayIndex(timezone: string | null): number {
  const zone = timezone || 'UTC';
  const short = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: zone }).format(new Date());
  return ({ Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 } as Record<string, number>)[short] ?? 0;
}

function todayHours(
  openingHours: VenueOpeningHour[] | LegacyVenueOpeningHours | null | undefined,
  timezone: string | null | undefined,
): string | null {
  if (!openingHours) return null;
  const day = weekdayIndex(timezone ?? null);

  if (Array.isArray(openingHours)) {
    const row = openingHours.find((item) => item.day === day);
    if (!row) return null;
    if (row.is_closed) return 'Сегодня закрыто';
    if (!row.open || !row.close) return null;
    return `Сегодня ${row.open}–${row.close}`;
  }

  const keys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const row = openingHours[keys[day]];
  if (!row) return null;
  if (row.is_closed) return 'Сегодня закрыто';
  if (!row.open || !row.close) return null;
  return `Сегодня ${row.open}–${row.close}`;
}

function venueImage(detail: VenueDetailData | null): string | null {
  return detail?.avatar_url ?? null;
}

function SaveButton({
  authenticated,
  saved,
  saving,
  onToggle,
}: {
  authenticated: boolean;
  saved: boolean;
  saving: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className={saved ? 'map-ref-save is-saved' : 'map-ref-save'}
      type="button"
      aria-label={saved ? 'Убрать из сохранённых' : 'Сохранить место'}
      aria-pressed={saved}
      disabled={!authenticated || saving}
      onClick={onToggle}
      title={authenticated ? (saved ? 'Убрать из сохранённых' : 'Сохранить место') : 'Сохранение доступно после входа'}
    >
      <HopperIcon name="bookmark" size={18} />
    </button>
  );
}

function Gallery({ venueId, photos, hasMore }: { venueId: string; photos: VenuePhoto[]; hasMore: boolean }) {
  if (photos.length === 0) return null;

  return (
    <div className="map-ref-gallery" aria-label="Фото места">
      {photos.slice(0, 3).map((photo, index) => (
        <a
          className="map-ref-gallery__item"
          href={`/venue/${encodeURIComponent(venueId)}/media`}
          key={photo.id}
          aria-label="Открыть фото заведения"
        >
          <img src={photo.thumbnail_url ?? photo.url} alt="" loading="lazy" />
          {index === 2 && hasMore && <span className="map-ref-gallery__more">Ещё</span>}
        </a>
      ))}
    </div>
  );
}

function PriceMeta({ price }: { price: VerifiedReceiptPrice | null }) {
  const amount = verifiedReceiptPricePrimary(price);
  const evidence = verifiedReceiptPriceSecondary(price);

  if (!price || (!amount && price.tier === null)) return null;

  return (
    <span className="map-ref-price" title={evidence ?? 'По подтверждённым фискальным чекам'}>
      {price.tier !== null && <strong>{PRICE_TIER_LABELS[price.tier]}</strong>}
      {amount && <span>{amount}</span>}
    </span>
  );
}

function ScoreMeta({ detail }: { detail: VenueDetailData | null }) {
  if (!detail?.scores) return null;

  return (
    <div className="map-ref-scores" aria-label="Оценки Хоппера">
      <span><strong>{detail.scores.hopper_score.toFixed(1)}</strong> Hopper</span>
      <span><strong>{detail.scores.live_score.toFixed(1)}</strong> Live</span>
    </div>
  );
}

function SocialProof({ detail }: { detail: VenueDetailData | null }) {
  if (!detail) return null;

  const friends = detail.friends_who_saved.map((friend) => ({
    id: friend.id,
    name: friend.display_name?.trim() || 'Друг Хоппера',
    imageUrl: friend.avatar_url,
  }));

  if (friends.length > 0) {
    return (
      <div className="map-ref-social">
        <FriendAvatarsStack friends={friends} max={3} size={26} />
        <span>{detail.friends_who_saved_count} {detail.friends_who_saved_count === 1 ? 'друг сохранил' : 'друзей сохранили'}</span>
      </div>
    );
  }

  if (detail.saved_count > 0) {
    return (
      <div className="map-ref-social map-ref-social--quiet">
        <HopperIcon name="bookmark" size={15} />
        <span>Сохранили {detail.saved_count}</span>
      </div>
    );
  }

  return null;
}

function LoyaltyRow({ detail }: { detail: VenueDetailData | null }) {
  const loyalty = detail?.my_loyalty;
  if (!loyalty?.program_enabled) return null;

  const tier = loyalty.current_tier?.display_name?.trim() || null;
  return (
    <div className="map-ref-context-row">
      <HopperIcon name="level" size={16} />
      <div>
        <span>Мой статус здесь</span>
        <strong>{tier ?? `${loyalty.local_xp} локальных XP`}</strong>
      </div>
    </div>
  );
}

function MobileAddressRow({ venue }: { venue: MapPreviewVenue }) {
  return (
    <div className="map-ref-mobile-address">
      <HopperIcon name="pin" size={15} />
      <span>{venue.address ?? 'Адрес уточняется'}</span>
    </div>
  );
}

function DetailRows({ venue, detail }: { venue: MapPreviewVenue; detail: VenueDetailData | null }) {
  const hours = todayHours(detail?.opening_hours, detail?.timezone);

  return (
    <div className="map-ref-details">
      <div className="map-ref-detail-row">
        <HopperIcon name="pin" size={16} />
        <span>{venue.address ?? 'Адрес уточняется'}</span>
      </div>
      {hours && (
        <div className="map-ref-detail-row">
          <HopperIcon name="clock" size={16} />
          <span>{hours}</span>
        </div>
      )}
    </div>
  );
}

function QuickActions({ venueId, onOpen, onOpenOffers }: { venueId: string; onOpen: () => void; onOpenOffers: () => void }) {
  return (
    <div className="map-ref-quick-actions" aria-label="Разделы места">
      <a href={`/venue/${encodeURIComponent(venueId)}/media`}>
        <HopperIcon name="image" size={17} />
        <span>Фото</span>
      </a>
      <button type="button" onClick={onOpen}>
        <HopperIcon name="message" size={17} />
        <span>Впечатления</span>
      </button>
      <button type="button" onClick={onOpenOffers}>
        <HopperIcon name="gift" size={17} />
        <span>Предложения</span>
      </button>
    </div>
  );
}

export function DesktopMapVenuePreview(props: PreviewCommonProps) {
  const { venue, detail, loading, authenticated, saved, saving, onToggleSaved, onOpen, onOpenOffers } = props;
  const photoPreview = useSelectedVenuePhotos(venue.id);
  const imageUrl = venueImage(detail);

  return (
    <div className="map-ref-venue map-ref-venue--desktop">
      <div className="map-ref-identity">
        <div className="map-ref-identity__media">
          {imageUrl ? <img src={imageUrl} alt="" /> : <HopperIcon name="building" size={25} />}
        </div>
        <div className="map-ref-identity__copy">
          <strong>{venue.name ?? 'Место Хоппера'}</strong>
          <span>{categoryLabel(venue.category)}</span>
        </div>
        <SaveButton authenticated={authenticated} saved={saved} saving={saving} onToggle={onToggleSaved} />
      </div>

      <div className="map-ref-meta-line">
        <span className={`map-ref-open is-${venue.opening_status}`}>{openingStatusLabel(venue.opening_status)}</span>
        <PriceMeta price={venue.verified_receipt_price} />
      </div>

      <ScoreMeta detail={detail} />
      <Gallery venueId={venue.id} photos={photoPreview.items} hasMore={photoPreview.hasMore} />
      <QuickActions venueId={venue.id} onOpen={onOpen} onOpenOffers={onOpenOffers} />
      <DetailRows venue={venue} detail={detail} />
      <SocialProof detail={detail} />
      <LoyaltyRow detail={detail} />

      {loading && <span className="map-ref-loading">Обновляем данные места…</span>}

      <button className="map-ref-open-button" type="button" onClick={onOpen}>Открыть</button>
    </div>
  );
}

export function MobileMapVenuePreview(props: MobilePreviewProps) {
  const {
    venue,
    detail,
    loading,
    authenticated,
    saved,
    saving,
    onToggleSaved,
    onOpen,
    expanded,
    onClose,
  } = props;
  const photoPreview = useSelectedVenuePhotos(venue.id);
  const imageUrl = venueImage(detail);
  const statusParts = useMemo(() => [categoryLabel(venue.category), openingStatusLabel(venue.opening_status)], [venue.category, venue.opening_status]);

  return (
    <div className="map-ref-mobile-card">
      <div className="map-ref-mobile-card__top">
        <div className="map-ref-mobile-card__photo">
          {imageUrl ? <img src={imageUrl} alt="" /> : <HopperIcon name="building" size={23} />}
        </div>
        <div className="map-ref-mobile-card__identity">
          <strong>{venue.name ?? 'Место Хоппера'}</strong>
          <span>{statusParts.join(' · ')}</span>
          <div className="map-ref-mobile-card__price"><PriceMeta price={venue.verified_receipt_price} /></div>
        </div>
        <div className="map-ref-mobile-card__tools">
          <SaveButton authenticated={authenticated} saved={saved} saving={saving} onToggle={onToggleSaved} />
          <button className="map-ref-close" type="button" aria-label="Закрыть карточку" onClick={onClose}>
            <HopperIcon name="close" size={17} />
          </button>
        </div>
      </div>

      <ScoreMeta detail={detail} />
      <MobileAddressRow venue={venue} />
      <SocialProof detail={detail} />

      {expanded && (
        <div className="map-ref-mobile-card__expanded">
          <Gallery venueId={venue.id} photos={photoPreview.items} hasMore={photoPreview.hasMore} />
          <DetailRows venue={venue} detail={detail} />
          <LoyaltyRow detail={detail} />
          {loading && <span className="map-ref-loading">Обновляем данные места…</span>}
        </div>
      )}

      <div className="map-ref-mobile-card__actions">
        <button className="map-ref-route-soon" type="button" disabled>
          <HopperIcon name="routes" size={16} />
          <span>Маршрут</span>
          <small>Скоро</small>
        </button>
        <button className="map-ref-open-button" type="button" onClick={onOpen}>Открыть</button>
      </div>
    </div>
  );
}
